// src/pages/MarcarAsistencia.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import { CheckCircle2, Clock, XCircle, Check, SkipForward } from 'lucide-react';
import { asistenciaApi, authHelpers } from '../api/docenteApi';
import { interpretarMarcado, estadoConfirmado } from '../utils/confirmacionMarcado';
import { permiteOmitir } from '../utils/omitirAcademia';

const AVATAR_BASE_URL = import.meta.env.VITE_BASE_URL_AVATARES;
// Agrega este estado al inicio de tu componente

// ── Card estática del fondo del stack ────────────────────────────
// Sin motion values ni transforms reactivos: solo CSS estático para
// ahorrar GPU. El parent la usa para todas las cards excepto la del frente.
const StudentCardStatic = ({ est, avatarUrl, stackDepth = 0 }) => {
  const tilt = est.id?.charCodeAt(0) % 2 ? 3 : -3;
  return (
    <div
      className="absolute inset-0 origin-bottom rounded-2xl select-none"
      style={{
        gridRow: 1, gridColumn: 1,
        transform: `scale(0.96) rotate(${tilt}deg)`,
        boxShadow: '0 8px 16px -8px rgba(0,0,0,0.1)',
      }}
    >
      <div className="card bg-base-100 h-full overflow-hidden">
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none z-10"
          style={{
            background: `linear-gradient(135deg, hsl(var(--p) / ${Math.min(stackDepth * 0.07, 0.18)}), hsl(var(--s) / ${Math.min(stackDepth * 0.05, 0.12)}))`,
          }}
        />
        <figure className="px-8 pt-8">
          <div className="avatar">
            <div className="w-36 h-36 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2">
              {avatarUrl ? (
                <img src={avatarUrl} alt={est.nombreCompleto} draggable={false} className="pointer-events-none" />
              ) : (
                <div className="bg-base-300 w-full h-full flex items-center justify-center text-6xl">
                  {est.genero === 'FEMENINO' ? '👧' : '👦'}
                </div>
              )}
            </div>
          </div>
        </figure>
        <div className="card-body items-center text-center pt-4 pb-2">
          <h2 className="card-title text-xl">{est.nombreCompleto}</h2>
          <p className="text-base-content/60 text-sm">Cédula: {est.cedula}</p>
          {est.seccionesAsistencia?.length > 1 && (
            <p className="badge badge-outline badge-sm mt-2">{est.seccionesAsistencia.map((s) => `${s.nivel} ${s.seccion}`).join(' · ')}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Card individual con swipe (solo la del frente) ───────────────
const StudentCard = ({ est, avatarUrl, isFront, stackDepth = 0, onSwipe, onOmitir, mostrarOmitir = false, guardando, rejectSignal = 0, getEstadoBadge }) => {
  const x = useMotionValue(0);

  // Estado del flash que cubre la card al marcar/omitir.
  // 'PRESENTE' | 'AUSENTE' | 'TARDANZA' | 'OMITIR' | null
  const [flashEstado, setFlashEstado] = useState(null);

  const rotateRaw = useTransform(x, [-150, 150], [-18, 18]);
  const presenteOpacity  = useTransform(x, [30, 80],   [0, 1]);
  const ausenteOpacity   = useTransform(x, [-30, -80], [0, 1]);
  // El pill "Omitir" se oculta apenas el docente empieza a arrastrar para que
  // no tape los overlays de PRESENTE/AUSENTE.
  const omitirOpacity    = useTransform(x, [-30, -10, 0, 10, 30], [0, 1, 1, 1, 0]);

  const rotate = useTransform(() => {
    const offset = isFront ? 0 : est.id?.charCodeAt(0) % 2 ? 3 : -3;
    return `${rotateRaw.get() + offset}deg`;
  });

  // Si la acción falla en el padre, guardando vuelve a false sin que la card
  // se desmonte. Limpiamos el flash para que el docente pueda reintentar.
  useEffect(() => {
    if (!guardando && flashEstado) {
      setFlashEstado(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardando]);

  // El padre puede rechazar el swipe sin entrar al flujo de guardando
  // (p. ej. estudiante ya marcado como Presente). Cuando eso pasa, bumpea
  // rejectSignal y aquí limpiamos el flash para no dejar el overlay atorado.
  useEffect(() => {
    if (rejectSignal > 0) {
      setFlashEstado(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejectSignal]);

  // Dispara la acción + muestra el flash. El padre retrasa la actualización
  // del stack 1s, así el overlay se ve por el tiempo completo. Si el API
  // tarda más de 1s, el flash se mantiene hasta que el padre desmonte la card.
  const triggerAction = (estado) => {
    if (guardando || flashEstado) return;
    setFlashEstado(estado);
    onSwipe(estado);
  };

  const handleDragEnd = () => {
    const ox = x.get();
    // Forzamos el snap-back a 0 explícitamente. dragElastic + drag=false al
    // disparar flashEstado a veces dejaba la card desplazada en el simulador
    // (lag de GPU); con animate() el retorno es determinista en todo dispositivo.
    animate(x, 0, { type: 'spring', stiffness: 300, damping: 30 });
    if (ox > 80) {
      triggerAction('PRESENTE');
    } else if (ox < -80) {
      triggerAction('AUSENTE');
    }
  };

  const handleOmitirClick = (e) => {
    e.stopPropagation();
    if (guardando || flashEstado) return;
    setFlashEstado('OMITIR');
    onOmitir();
  };

  // Configuración visual del overlay según el estado.
  // OMITIR usa Tailwind class bg-base-200 (variación leve sobre el bg-base-100
  // del card) para que sea theme-aware (oscuro en dark, claro en light) sin
  // chocar visualmente. Para PRESENTE/AUSENTE/TARDANZA seguimos con colores
  // semánticos universales como rgba inline.
  const flashConfig = {
    PRESENTE: { bg: 'rgba(40,167,69,0.80)',  text: 'text-white',                  label: 'PRESENTE'   },
    AUSENTE:  { bg: 'rgba(220,53,69,0.80)',  text: 'text-white',                  label: 'AUSENTE'    },
    TARDANZA: { bg: 'rgba(245,158,11,0.80)', text: 'text-white',                  label: 'TARDANZA'   },
    OMITIR:   { bgClass: 'bg-base-200/80 backdrop-blur-sm', text: 'text-base-content/70 italic', label: 'Omitiendo…' },
  };

  return (
    <motion.div
      className="absolute inset-0 origin-bottom rounded-2xl touch-none select-none"
      style={{
        gridRow: 1, gridColumn: 1,
        x, rotate,
        boxShadow: isFront
          ? '0 20px 30px -10px rgba(0,0,0,0.25)'
          : '0 8px 16px -8px rgba(0,0,0,0.1)',
      }}
      initial={isFront ? { opacity: 0, scale: 0.95 } : false}
      animate={{ opacity: 1, scale: isFront ? 1 : 0.96 }}
      transition={{ duration: 0.28, ease: 'easeOut' }}
      drag={isFront && !flashEstado ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={1}
      dragMomentum={false}
      onDragEnd={handleDragEnd}
    >
      <div className="card bg-base-100 h-full overflow-hidden">
        {/* Tinte de profundidad para cards del stack */}
        {!isFront && (
          <div
            className="absolute inset-0 rounded-2xl pointer-events-none z-10"
            style={{
              background: `linear-gradient(135deg, hsl(var(--p) / ${Math.min(stackDepth * 0.07, 0.18)}), hsl(var(--s) / ${Math.min(stackDepth * 0.05, 0.12)}))`,
            }}
          />
        )}
        {/* Overlay PRESENTE - POSICIÓN BAJA */}
        <motion.div
          className="absolute left-4 right-4 flex items-center justify-center pointer-events-none z-20 rounded-xl py-2"
          style={{ 
            opacity: presenteOpacity, 
            bottom: '7.5rem',  /* 28 + padding dinámico */
            backgroundColor: 'rgba(40,167,69,0.9)',
            boxShadow: '0 4px 12px rgba(40,167,69,0.4)'
          }}
        >
          <span className="text-white text-lg font-bold drop-shadow-lg flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            PRESENTE
          </span>
        </motion.div>

        {/* Overlay AUSENTE - POSICIÓN BAJA */}
        <motion.div
          className="absolute left-4 right-4 flex items-center justify-center pointer-events-none z-20 rounded-xl py-2"
          style={{
            opacity: ausenteOpacity,
            bottom: '7.5rem',  /* 28 + padding dinámico */
            backgroundColor: 'rgba(220,53,69,0.9)',
            boxShadow: '0 4px 12px rgba(220,53,69,0.4)'
          }}
        >
          <span className="text-white text-lg font-bold drop-shadow-lg flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            AUSENTE
          </span>
        </motion.div>

        {/* Pill "Omitir para el siguiente grupo": flotante arriba (no ocupa
            espacio del card-body) y se oculta apenas se arrastra para no
            tapar los overlays de PRESENTE/AUSENTE. Solo en bloques compartidos.
            El wrapper externo se encarga del centrado (left-1/2 + translateX);
            el botón interno se encarga del pop-in/tap sin pisar el transform
            de centrado. */}
        {isFront && mostrarOmitir && (
          <motion.div
            className="absolute top-3 left-1/2 z-30"
            style={{ opacity: omitirOpacity, translateX: '-50%' }}
          >
            <motion.button
              type="button"
              onClick={handleOmitirClick}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={guardando || !!flashEstado}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 420, damping: 22, delay: 0.12 }}
              whileTap={{ scale: 1.08 }}
              className="btn btn-sm h-9 min-h-9 normal-case flex-nowrap gap-1.5 text-xs font-semibold whitespace-nowrap px-3 bg-base-100 hover:bg-base-200 text-accent border-2 border-accent/50 shadow-sm"
            >
              <SkipForward className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
              Omitir para el siguiente grupo
            </motion.button>
          </motion.div>
        )}

        {/* En bloque compartido el pill "Omitir" (btn-sm, top-3) ocupa hasta
            ~y=44; empujamos el avatar más abajo para no superponerse. */}
        <figure className={`px-8 shrink-0 ${mostrarOmitir ? 'pt-16' : 'pt-8'}`}>
          <div className="avatar">
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2">
              {avatarUrl ? (
                <img src={avatarUrl} alt={est.nombreCompleto} draggable={false} className="pointer-events-none" />
              ) : (
                <div className="bg-base-300 w-full h-full flex items-center justify-center text-6xl">
                  {est.genero === 'FEMENINO' ? '👧' : '👦'}
                </div>
              )}
            </div>
          </div>
        </figure>

        <div className="card-body items-center text-center pt-3 pb-2 min-h-0 flex-1 overflow-hidden">
          <h2 className="card-title text-base sm:text-xl leading-tight line-clamp-2 break-words text-center">
            {est.nombreCompleto}
          </h2>
          <p className="text-base-content/60 text-sm">Cédula: {est.cedula}</p>
          {est.seccionesAsistencia?.length > 1 && (
            <p className="badge badge-outline badge-sm mt-2">{est.seccionesAsistencia.map((s) => `${s.nivel} ${s.seccion}`).join(' · ')}</p>
          )}

          {est.asistenciaMarcada && (
            <div className="mt-3">{getEstadoBadge(est.estadoAsistencia)}</div>
          )}

          {guardando && isFront && (
            <div className="mt-3">
              <span className="loading loading-spinner loading-md"></span>
            </div>
          )}

          {/* BOTONES INTEGRADOS EN LA CARD - SÓLO CAMBIA ESTA SECCIÓN */}
          {isFront && (
            <div className="grid grid-cols-3 gap-2 w-full mt-auto pt-2 px-2 relative z-20">
              {/* AUSENTE */}
              <motion.button
                className="btn btn-circle btn-xs h-14 w-14 p-0 mx-auto bg-base-100/90 hover:bg-base-200 border-2 border-base-300 group relative overflow-hidden"
                onClick={() => triggerAction('AUSENTE')}
                disabled={guardando}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.95 }}
              >
                {/* Fondo dinámico de swipe */}
                <motion.div
                  className="absolute inset-0 rounded-full z-0"
                  style={{ 
                    opacity: ausenteOpacity,
                    backgroundColor: 'rgba(220,53,69,1)'
                  }}
                  transition={{ duration: 0.15 }}
                />
                
                {/* Icono */}
                <svg className="w-6 h-6 stroke-current stroke-base-content/70 group-hover:stroke-error z-10 relative" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </motion.button>
              
              {/* TARDANZA */}
              <motion.button
                className="btn btn-circle btn-xs h-14 w-14 p-0 mx-auto bg-base-100/90 hover:bg-base-200 border-2 border-base-300 group relative overflow-hidden"
                onClick={() => triggerAction('TARDANZA')}
                disabled={guardando}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg className="w-6 h-6 stroke-current stroke-base-content/70 group-hover:stroke-warning z-10 relative" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
              </motion.button>
              
              {/* PRESENTE */}
              <motion.button
                className="btn btn-circle btn-xs h-14 w-14 p-0 mx-auto bg-base-100/90 hover:bg-base-200 border-2 border-base-300 group relative overflow-hidden"
                onClick={() => triggerAction('PRESENTE')}
                disabled={guardando}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.95 }}
              >
                {/* Fondo dinámico de swipe */}
                <motion.div
                  className="absolute inset-0 rounded-full z-0"
                  style={{ 
                    opacity: presenteOpacity,
                    backgroundColor: 'rgba(40,167,69,1)'
                  }}
                  transition={{ duration: 0.15 }}
                />
                
                {/* Icono */}
                <svg className="w-6 h-6 stroke-current stroke-base-content/70 group-hover:stroke-success z-10 relative" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7"/>
                </svg>
              </motion.button>
            </div>
          )}

          {/* Indicadores de dirección */}
          <div className="flex justify-between w-full pt-2 text-xs text-base-content/30 select-none">
            <span>← Ausente</span>
            <span>Presente →</span>
          </div>
        </div>

        {/* Flash overlay: cubre toda la card por 1s con el color del estado.
            Para OMITIR muestra "Omitiendo…" en gris para sugerir que el
            contenido se desvanece. */}
        {flashEstado && (
          <motion.div
            className={`absolute inset-0 z-40 rounded-2xl flex items-center justify-center pointer-events-none ${flashConfig[flashEstado].bgClass || ''}`}
            style={flashConfig[flashEstado].bg ? { backgroundColor: flashConfig[flashEstado].bg } : undefined}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            <span className={`text-3xl font-bold drop-shadow-lg ${flashConfig[flashEstado].text}`}>
              {flashConfig[flashEstado].label}
            </span>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

const MarcarAsistencia = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { horario, horariosSimultaneos = [], fecha, anioEscolar: periodoNavegacion, respetarLimite = true, esAcademia = false } = location.state || {};
  const anioEscolar = horario?.AnioEscolar || periodoNavegacion;
  const clasesActivas = horariosSimultaneos.length ? horariosSimultaneos : (horario ? [horario] : []);
  const esMarcadoConjunto = clasesActivas.length > 1;
  // Modo flexible = el colegio tiene desactivado "respetar límite de horas".
  // El backend respeta el flag al guardar; aquí solo lo usamos para informar
  // al docente con un indicador visual.
  const modoFlexible = respetarLimite === false;

  const [estudiantes, setEstudiantes]     = useState([]);
  const [indiceActual, setIndiceActual]   = useState(0);
  const [loading, setLoading]             = useState(true);
  const [guardando, setGuardando]         = useState(false);
  const [error, setError]                 = useState('');
  const [mostrarResumen, setMostrarResumen] = useState(false);
  const [cardStack, setCardStack]         = useState([]);
  // Contador que se incrementa cuando rechazamos un swipe sin entrar al flujo
  // de guardando. Le sirve al hijo (StudentCard) para limpiar su flash overlay.
  const [rejectSignal, setRejectSignal]   = useState(0);

  const [filtroActivo, setFiltroActivo] = useState(null);

  const toggleFiltro = (estado) => {
    setFiltroActivo(prev => prev === estado ? null : estado);
  };


  useEffect(() => {
    if (!horario || !fecha) {
      navigate('/mis-clases');
      return;
    }
    cargarEstudiantes();
  }, []);

  const cargarEstudiantes = async () => {
    setLoading(true);
    setError('');

    try {
      const sesion = await authHelpers.refrescarSesion();
      if (!sesion) {
        navigate('/login');
        return;
      }

      let estudiantesEnriquecidos;
      if (esMarcadoConjunto) {
        // Un único request, roster leído una sola vez y sin URLs de foto para
        // evitar descargas masivas cuando el docente usa datos móviles.
        const conjunta = await asistenciaApi.getEstudiantesConjuntas(sesion.colegioID, anioEscolar, fecha,
          clasesActivas.map((c) => ({ horarioID: c.HorarioID, nivelAcademico: c.NivelAcademico, seccion: c.Seccion, materia: c.Materia })));
        estudiantesEnriquecidos = conjunta.estudiantes || [];
      } else {
        const response = await asistenciaApi.getEstudiantesClase(sesion.colegioID, horario.AnioEscolar,
          horario.NivelAcademico, horario.Seccion, horario.HorarioID, sesion.docente.docenteID || sesion.docente.id, fecha);
        estudiantesEnriquecidos = response.estudiantes || [];
      }
      if (!esMarcadoConjunto && horario.BloqueCompartido) {
        estudiantesEnriquecidos = estudiantesEnriquecidos.map(estudiante => {
          if (estudiante.asistenciaMarcada && estudiante.materiaIDAsistencia !== horario.MateriaID) {
            return {
              ...estudiante,
              asistenciaMarcada: false,
              estadoAsistencia: null,
              horaMarcacion: null,
              observaciones: null,
            };
          }
          return estudiante;
        });
      }

      setEstudiantes(estudiantesEnriquecidos);

      // Stack siempre fresco desde la API: solo pendientes sin marcar
      const pendientes = estudiantesEnriquecidos
        .filter(e => !e.asistenciaMarcada)
        .map(e => e.id);
      setCardStack(pendientes);

      const primerPendiente = estudiantesEnriquecidos.findIndex(e => !e.asistenciaMarcada);
      if (primerPendiente !== -1) setIndiceActual(primerPendiente);

      // Si la clase entera ya está marcada (entramos desde el botón "Ver Resumen"
      // de MisClasesHoy), abrir directo el resumen sin pasar por el stack vacío.
      if (estudiantesEnriquecidos.length > 0 && pendientes.length === 0) {
        setMostrarResumen(true);
      }

    } catch (err) {
      console.error('Error cargando estudiantes:', err);
      setError(err.message || 'Error al cargar estudiantes');
    } finally {
      setLoading(false);
    }
  };

  // ── Swipe handler: marca y elimina del stack ─────────────────────
  const handleSwipe = async (estId, estado) => {
    if (guardando) return;

    const idx = estudiantes.findIndex(e => e.id === estId);
    const est = estudiantes[idx];
    if (!est) {
      setRejectSignal(v => v + 1);
      return;
    }

    // Libertad total: el docente puede corregir/cambiar el estado de cualquier
    // estudiante las veces que haga falta, aunque ya esté Presente o con Tardanza
    // (para arreglar errores). No se bloquea ninguna transición.

    setGuardando(true);

    try {
      const sesion = await authHelpers.refrescarSesion();
      // Forzamos un piso de 700ms para que el flash overlay del StudentCard
      // se vea completo aunque el API sea instantáneo. Si el API tarda más,
      // el flash se mantiene hasta que el padre desmonte la card.
      const minDelay = new Promise(r => setTimeout(r, 700));
      const secciones = esMarcadoConjunto ? est.seccionesAsistencia : [{
        horarioID: horario.HorarioID, nivel: horario.NivelAcademico, seccion: horario.Seccion,
      }];
      const confirmaciones = { ...(est.confirmacionesMarcado || {}) };
      const mismaIntencion = marca => marca?.estadoSolicitado === estado && marca.fecha === fecha &&
        marca.colegioID === sesion.colegioID && marca.anioEscolar === anioEscolar;
      const pendientes = secciones.filter(seccion => !mismaIntencion(confirmaciones[seccion.horarioID]));
      const apiCall = pendientes.length === 0 ? Promise.resolve(null) : esMarcadoConjunto
        ? asistenciaApi.marcarAsistenciaConjunta(
          sesion.colegioID, sesion.docente.docenteID || sesion.docente.id, fecha,
          pendientes.map((s) => ({ horarioID: s.horarioID, asistencias: [{ estudianteID: est.id, estado }] })),
          anioEscolar
        )
        : asistenciaApi.marcarAsistencia(horario.HorarioID, sesion.colegioID,
          sesion.docente.docenteID || sesion.docente.id, fecha, est.id, estado, anioEscolar, '');
      const [response] = await Promise.all([apiCall, minDelay]);
      const resultado = pendientes.length ? interpretarMarcado(response, {
        estudianteID: est.id, horariosIDs: pendientes.map(seccion => seccion.horarioID),
        colegioID: sesion.colegioID, fecha, anioEscolar,
      }) : { confirmadas: [], fallidas: [] };
      for (const marca of resultado.confirmadas) {
        confirmaciones[marca.horarioID] = { estadoSolicitado: estado, estado: marca.estado,
          fecha, colegioID: sesion.colegioID, anioEscolar };
      }
      // Solo los éxitos de este intento (o del intento parcial anterior) pueden
      // releerse como confirmados; un roster previo no convierte un fallo en éxito.
      for (const seccion of secciones) {
        const marca = confirmaciones[seccion.horarioID];
        if (!mismaIntencion(marca) || estadoConfirmado(marca.estado)) continue;
        try {
          const roster = await asistenciaApi.getEstudiantesClase(sesion.colegioID, anioEscolar,
            seccion.nivel, seccion.seccion, seccion.horarioID,
            sesion.docente.docenteID || sesion.docente.id, fecha);
          const alumnos = roster.estudiantes?.filter(item => item.id === est.id) || [];
          const alumno = alumnos.length === 1 ? alumnos[0] : null;
          if (alumno?.asistenciaMarcada && estadoConfirmado(alumno.estadoAsistencia)) {
            marca.estado = alumno.estadoAsistencia;
          }
        } catch {
          // La escritura está acreditada, pero su estado aún no: conservar la
          // tarjeta y volver a leer sin reenviar esta materia en el reintento.
        }
      }
      const seccionesActualizadas = secciones.map(seccion => {
        const marca = confirmaciones[seccion.horarioID];
        const confirmada = mismaIntencion(marca) && Boolean(estadoConfirmado(marca.estado));
        return { ...seccion, asistenciaMarcada: confirmada || seccion.asistenciaMarcada === true,
          estadoAsistencia: confirmada ? marca.estado : seccion.estadoAsistencia || null };
      });
      const pendientesIntencion = secciones.filter(seccion => {
        const marca = confirmaciones[seccion.horarioID];
        return !mismaIntencion(marca) || !estadoConfirmado(marca.estado);
      });
      const completa = pendientesIntencion.length === 0;
      const estadoFinal = completa ? seccionesActualizadas[0].estadoAsistencia : est.estadoAsistencia || null;
      const nuevosEstudiantes = [...estudiantes];
      nuevosEstudiantes[idx] = { ...est, confirmacionesMarcado: completa ? {} : confirmaciones,
        ...(esMarcadoConjunto && { seccionesAsistencia: seccionesActualizadas }),
        asistenciaMarcada: completa || est.asistenciaMarcada === true, estadoAsistencia: estadoFinal };
      setEstudiantes(nuevosEstudiantes);
      if (!completa) {
        const pendientesCantidad = pendientesIntencion.length;
        const materiasPendientes = pendientesIntencion.map(seccion => seccion.materia ||
          [seccion.nivel, seccion.seccion].filter(Boolean).join(' ')).filter(Boolean).join(', ');
        const mensaje = resultado.fallidas[0]?.mensaje || 'No se pudo comprobar el estado registrado. Vuelve a intentarlo.';
        setError(`${pendientesCantidad} ${pendientesCantidad === 1 ? 'materia pendiente' : 'materias pendientes'}${materiasPendientes ? `: ${materiasPendientes}` : ''}. ${mensaje}`);
        return;
      }
      setError('');

      // Quitar del stack
      const stackSinEste = cardStack.filter(id => id !== estId);
      setCardStack(stackSinEste);

      if (stackSinEste.length === 0) {
        setTimeout(() => setMostrarResumen(true), 400);
      } else {
        const siguienteId  = stackSinEste[stackSinEste.length - 1];
        const siguienteIdx = estudiantes.findIndex(e => e.id === siguienteId);
        if (siguienteIdx !== -1) setIndiceActual(siguienteIdx);
      }

    } catch (err) {
      console.error('Error marcando asistencia:', err);
      setError(err.message || 'Error al marcar asistencia');
      setTimeout(() => setError(''), 4000);
    } finally {
      setGuardando(false);
    }
  };

  // ── Omitir para el siguiente grupo (solo bloques compartidos) ────
  // Saca al estudiante del stack pendiente sin marcar nada y sin llamar al
  // backend. Queda sin marcar para esta materia; el docente de la siguiente
  // materia del bloque lo verá pendiente cuando entre a marcar. El estado
  // se conserva solo en memoria — si el docente cierra y vuelve a entrar,
  // el estudiante reaparece como sin marcar (correcto). Mantenemos guardando
  // en true durante 700ms para que el flash "Omitiendo…" del card se vea
  // antes del pop-in del siguiente estudiante.
  const handleOmitir = (estId) => {
    if (guardando || !permiteOmitir(horario, esAcademia)) return;
    setGuardando(true);

    setTimeout(() => {
      const stackSinEste = cardStack.filter(id => id !== estId);
      setCardStack(stackSinEste);

      if (stackSinEste.length === 0) {
        setTimeout(() => setMostrarResumen(true), 400);
      } else {
        const siguienteId  = stackSinEste[stackSinEste.length - 1];
        const siguienteIdx = estudiantes.findIndex(e => e.id === siguienteId);
        if (siguienteIdx !== -1) setIndiceActual(siguienteIdx);
      }
      setGuardando(false);
    }, 700);
  };

  // ── Desde resumen: ir a un estudiante específico ─────────────────
  // Reconstruye el stack poniendo al estudiante seleccionado al frente
  const irAEstudiante = (index) => {
    const estudianteTarget = estudiantes[index];
    if (!estudianteTarget) return;

    // Construir un stack que ponga al estudiante target al frente.
    // Incluimos todos los pendientes + el target si ya fue marcado (para rectificar).
    const yaEnStack = cardStack.includes(estudianteTarget.id);
    let nuevoStack;

    if (yaEnStack) {
      // Rotar el stack para que el target quede al final (frente)
      const sinTarget = cardStack.filter(id => id !== estudianteTarget.id);
      nuevoStack = [...sinTarget, estudianteTarget.id];
    } else {
      // Era un marcado: reinsertarlo al frente del stack actual
      nuevoStack = [...cardStack, estudianteTarget.id];
    }

    setCardStack(nuevoStack);
    setIndiceActual(index);
    setFiltroActivo(null);
    setMostrarResumen(false);
  };

  const calcularEstadisticas = () => {
    const presentes  = estudiantes.filter(e => e.estadoAsistencia === 'PRESENTE').length;
    const tardanzas  = estudiantes.filter(e => e.estadoAsistencia === 'TARDANZA').length;
    const ausentes   = estudiantes.filter(e => e.estadoAsistencia === 'AUSENTE').length;
    const pendientes = estudiantes.filter(e => !e.asistenciaMarcada).length;
    return { presentes, tardanzas, ausentes, pendientes, total: estudiantes.length };
  };

  const finalizarAsistencia = () => {
    navigate('/mis-clases');
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'PRESENTE':
        return (
          <span className="badge badge-success badge-lg gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />
            Presente
          </span>
        );
      case 'TARDANZA':
        return (
          <span className="badge badge-warning badge-lg gap-1">
            <Clock className="w-3.5 h-3.5" strokeWidth={2} />
            Tardanza
          </span>
        );
      case 'AUSENTE':
        return (
          <span className="badge badge-error badge-lg gap-1">
            <XCircle className="w-3.5 h-3.5" strokeWidth={2} />
            Ausente
          </span>
        );
      default:
        return null;
    }
  };

  // ── Loading / Error / Sin estudiantes ────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="cp-mark-logo" />
      </div>
    );
  }

  if (error && !estudiantes.length) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="alert alert-error max-w-md"
        >
          <span>{error}</span>
        </motion.div>
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn btn-primary mt-4"
          onClick={() => navigate('/mis-clases')}
        >
          Volver
        </motion.button>
      </div>
    );
  }

  if (estudiantes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h2 className="text-2xl font-bold mb-4">No hay estudiantes</h2>
          <p className="text-base-content/60 mb-4">No se encontraron estudiantes para esta clase</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn btn-primary"
            onClick={() => navigate('/mis-clases')}
          >
            Volver
          </motion.button>
        </motion.div>
      </div>
    );
  }

  const stats = calcularEstadisticas();
  //console.log('🔍 mostrarResumen:', mostrarResumen, '| stats:', stats);
  // ── Vista de resumen MOBILE ─────────────────────────────────────────────
  if (mostrarResumen) {
      const estudiantesOrdenados = [...estudiantes].sort((a, b) => {
        const orden = { sin_marcar: 0, AUSENTE: 1, TARDANZA: 2, PRESENTE: 3, JUSTIFICADO: 4 };
        const estadoA = !a.asistenciaMarcada ? 'sin_marcar' : a.estadoAsistencia;
        const estadoB = !b.asistenciaMarcada ? 'sin_marcar' : b.estadoAsistencia;
        const ordenA  = orden[estadoA] ?? 999;
        const ordenB  = orden[estadoB] ?? 999;
        if (ordenA === ordenB) return a.nombreCompleto.localeCompare(b.nombreCompleto);
        return ordenA - ordenB;
      });

      const estudiantesFiltrados = filtroActivo
        ? estudiantesOrdenados.filter(e => {
            if (filtroActivo === 'SIN_MARCAR') return !e.asistenciaMarcada;
            return e.estadoAsistencia === filtroActivo;
          })
        : estudiantesOrdenados;

      return (
        <div className="h-dvh bg-base-200 flex flex-col overflow-hidden">
          {/* Header fijo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="navbar bg-base-300 text-base-content shadow-lg px-4 py-2 safe-top"
          >
            <div className="flex-1">
              <div className="flex flex-col gap-0.5">
                <span className="text-lg font-bold leading-tight">Resumen</span>
                <span className="text-xs opacity-70 leading-tight">{horario.Materia}</span>
              </div>
            </div>
          </motion.div>

          <div className="flex-1 flex flex-col overflow-hidden px-4 py-3 space-y-3">
            {/* Stats con filtro */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="stats stats-horizontal shadow bg-base-100 rounded-xl p-1.5 mb-4"
            >
              {/* Presentes */}
              <div
                className={`stat place-items-center p-1.5 rounded-lg cursor-pointer transition-all flex-1 group
                  ${filtroActivo === 'PRESENTE' ? 'bg-success/20 ring-2 ring-success/40' : 'hover:bg-success/10'}`}
                onClick={() => toggleFiltro('PRESENTE')}
              >
                <div className={`stat-figure transition-transform group-hover:scale-110 ${filtroActivo === 'PRESENTE' ? 'text-success scale-110' : 'text-success'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-7 h-7 stroke-current">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="stat-value text-xl font-bold">{stats.presentes}</div>
              </div>

              {/* Tardanzas */}
              <div
                className={`stat place-items-center p-1.5 rounded-lg cursor-pointer transition-all flex-1 group
                  ${filtroActivo === 'TARDANZA' ? 'bg-warning/20 ring-2 ring-warning/40' : 'hover:bg-warning/10'}`}
                onClick={() => toggleFiltro('TARDANZA')}
              >
                <div className={`stat-figure transition-transform group-hover:scale-110 ${filtroActivo === 'TARDANZA' ? 'text-warning scale-110' : 'text-warning'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-7 h-7 stroke-current">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="stat-value text-xl font-bold">{stats.tardanzas}</div>
              </div>

              {/* Ausentes */}
              <div
                className={`stat place-items-center p-1.5 rounded-lg cursor-pointer transition-all flex-1 group
                  ${filtroActivo === 'AUSENTE' ? 'bg-error/20 ring-2 ring-error/40' : 'hover:bg-error/10'}`}
                onClick={() => toggleFiltro('AUSENTE')}
              >
                <div className={`stat-figure transition-transform group-hover:scale-110 ${filtroActivo === 'AUSENTE' ? 'text-error scale-110' : 'text-error'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-7 h-7 stroke-current">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div className="stat-value text-xl font-bold">{stats.ausentes}</div>
              </div>

              {/* Sin marcar */}
              <div
                className={`stat place-items-center p-1.5 rounded-lg cursor-pointer transition-all flex-1 group
                  ${filtroActivo === 'SIN_MARCAR' ? 'bg-base-300 ring-2 ring-base-content/20' : 'hover:bg-base-200/50'}`}
                onClick={() => toggleFiltro('SIN_MARCAR')}
              >
                <div className={`stat-figure transition-transform group-hover:scale-110 ${filtroActivo === 'SIN_MARCAR' ? 'text-base-content/70 scale-110' : 'text-base-content/40'}`}>
                  {stats.pendientes > 0 ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-7 h-7 stroke-current">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-7 h-7 stroke-current">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <div className="stat-value text-xl font-bold text-base-content/40">{stats.pendientes}</div>
              </div>
            </motion.div>

            {/* Lista de estudiantes */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.07, duration: 0.25, ease: 'easeOut' }}
              className="flex-1 card bg-base-100 shadow-sm overflow-hidden"
            >
              <div className="card-body p-3 h-full flex flex-col">
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-base-200">
                  <h3 className="font-bold text-sm">Estudiantes</h3>
                  <span className="text-xs text-base-content/50">Tap para editar</span>
                </div>

                <div className="flex-1 overflow-y-auto space-y-1.5 pt-1 scrollbar-thin scrollbar-thumb-base-300 scrollbar-track-base-100">
                  {estudiantesFiltrados.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-8 text-base-content/40">
                      <p className="text-sm">Sin resultados para este filtro</p>
                      <button className="btn btn-ghost btn-xs mt-2" onClick={() => setFiltroActivo(null)}>Ver todos</button>
                    </div>
                  )}
                  {estudiantesFiltrados.map((estudiante, index) => {
                    const avatarUrl = estudiante.foto
                      ? `${AVATAR_BASE_URL}avatares/${estudiante.foto}`
                      : null;
                    const realIndex = estudiantes.findIndex(e => e.id === estudiante.id);

                    return (
                      <motion.div
                        key={estudiante.id}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: Math.min(index * 0.015, 0.3), duration: 0.15, ease: 'easeOut' }}
                        onClick={() => irAEstudiante(realIndex)}
                        className="flex items-center gap-2.5 p-2.5 hover:bg-base-200/50 active:bg-base-200 rounded-xl cursor-pointer transition-all border border-base-200/50 hover:border-base-300"
                      >
                        <div className="avatar flex-shrink-0">
                          <div className="w-10 h-10 rounded-full ring-1 ring-base-200">
                            {avatarUrl ? (
                              <img src={avatarUrl} alt={estudiante.nombreCompleto} className="object-cover" />
                            ) : (
                              <div className="bg-gradient-to-br from-base-200 to-base-300 w-full h-full flex items-center justify-center rounded-full">
                                <span className="text-lg">
                                  {estudiante.genero === 'FEMENINO' ? '👧' : '👦'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex-1 min-w-0 pr-2">
                          <p className="font-semibold text-sm leading-tight truncate">
                            {estudiante.nombreCompleto}
                          </p>
                          <p className="text-xs text-base-content/60 truncate">
                            {estudiante.cedula}
                          </p>
                        </div>

                        <div className="flex-shrink-0 ml-1">
                          {!estudiante.asistenciaMarcada ? (
                            <span className="badge badge-ghost badge-sm text-xs px-2.5 py-1.5">Sin marcar</span>
                          ) : estudiante.estadoAsistencia === 'PRESENTE' ? (
                            <span className="badge badge-success badge-sm text-xs px-2.5 py-1.5 font-bold">Presente</span>
                          ) : estudiante.estadoAsistencia === 'TARDANZA' ? (
                            <span className="badge badge-warning badge-sm text-xs px-2.5 py-1.5 font-bold">Tardanza</span>
                          ) : (
                            <span className="badge badge-error badge-sm text-xs px-2.5 py-1.5 font-bold">Ausente</span>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>

            {/* Botón finalizar */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.14, duration: 0.25, ease: 'easeOut' }}
              className="pt-1 pb-3 px-1"
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn btn-primary w-full h-12 text-sm font-bold shadow-lg gap-2"
                onClick={finalizarAsistencia}
              >
                <Check className="w-4 h-4" strokeWidth={2.5} />
                Finalizar Asistencia
              </motion.button>
            </motion.div>
          </div>
        </div>
      );
    }

  // ── Vista principal de swipe ─────────────────────────────────────
  return (
    <div className="min-h-dvh bg-base-200 flex flex-col overflow-hidden safe-x">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-base-100 shadow-md px-4 pb-4 flex-shrink-0 safe-top"
      >
        <div className="max-w-2xl mx-auto pt-3 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold leading-tight">{esMarcadoConjunto ? 'Asistencia conjunta' : horario.Materia}</h1>
              {esMarcadoConjunto && <div className="badge badge-outline badge-primary badge-sm">{clasesActivas.length} secciones</div>}
              {modoFlexible && (
                <div
                  className="tooltip tooltip-bottom cursor-help before:!left-0 before:!translate-x-0 before:max-w-[min(260px,calc(100vw-2rem))] before:whitespace-pre-wrap before:text-left"
                  data-tip="Modo flexible activado: tu colegio permite marcar asistencia en cualquier momento del día (después del inicio de cada clase)."
                >
                  <div className="badge badge-success badge-sm gap-1 font-semibold">
                    <Clock className="w-3 h-3" strokeWidth={2.5} />
                    Modo flexible
                  </div>
                </div>
              )}
            </div>
            <p className="text-sm opacity-70 mt-0.5">{esMarcadoConjunto ? clasesActivas.map((c) => `${c.NivelAcademico} ${c.Seccion}`).join(' · ') : `${horario.NivelAcademico} ${horario.Seccion}`}</p>
          </div>
          <motion.button
            whileHover={{ x: 3 }}
            whileTap={{ scale: 0.95 }}
            className="btn btn-ghost btn-sm flex-shrink-0 mt-0.5"
            onClick={() => navigate('/mis-clases')}
          >
            Volver →
          </motion.button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="max-w-2xl mx-auto px-5 py-3 w-full flex-shrink-0"
      >
        <div className="flex justify-between text-sm mb-1">
          <span>Estudiante {indiceActual + 1} de {estudiantes.length}</span>
          <span>{stats.presentes + stats.tardanzas + stats.ausentes} marcados</span>
        </div>
        <progress
          className="progress progress-primary w-full"
          value={stats.presentes + stats.tardanzas + stats.ausentes}
          max={estudiantes.length}
        />
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 left-0 right-0 px-4 z-30 flex justify-center pointer-events-none"
          >
            <div className="alert alert-error shadow-lg max-w-md py-2 px-4 pointer-events-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current flex-shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm">{error}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-grow flex flex-col items-center justify-center px-5 py-2 relative overflow-hidden">
        <div className="w-full max-w-sm relative grid place-items-center" style={{ height: 440 }}>
          {/* Solo las últimas 3 cards existen en el DOM: el resto no aporta
              nada visual pero sí consume GPU por capas apiladas. */}
          {cardStack.slice(-3).map(estId => {
            const est = estudiantes.find(e => e.id === estId);
            if (!est) return null;
            const estAvatar = est.foto ? `${AVATAR_BASE_URL}avatares/${est.foto}` : null;
            const isFront    = estId === cardStack[cardStack.length - 1];
            const stackDepth = cardStack.length - 1 - cardStack.indexOf(estId);
            if (!isFront) {
              return (
                <StudentCardStatic
                  key={estId}
                  est={est}
                  avatarUrl={estAvatar}
                  stackDepth={stackDepth}
                />
              );
            }
            return (
              <StudentCard
                key={estId}
                est={est}
                avatarUrl={estAvatar}
                isFront={isFront}
                stackDepth={stackDepth}
                guardando={guardando}
                rejectSignal={rejectSignal}
                getEstadoBadge={getEstadoBadge}
                onSwipe={estado => handleSwipe(estId, estado)}
                onOmitir={() => handleOmitir(estId)}
                mostrarOmitir={permiteOmitir(horario, esAcademia)}
              />
            );
          })}
          {cardStack.length === 0 && (
            <div className="text-center text-base-content/40">
              <p className="text-lg font-medium">Todos marcados</p>
            </div>
          )}
        </div>
      </div>

      {/* Boton de resumen */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="w-full max-w-sm mx-auto px-5 pb-8 pt-2 flex-shrink-0"
      >
        <button
          className="btn btn-primary btn-sm btn-block"
          onClick={() => setMostrarResumen(true)}
        >
          Ver Resumen ({stats.presentes + stats.tardanzas + stats.ausentes}/{estudiantes.length})
        </button>
      </motion.div>
    </div>
  );
};

export default MarcarAsistencia;
